/**
 * View-layer types for the Partner Data Gateway list pages.
 *
 * The transport-agnostic domain model (ingestions, batches, configurations,
 * etc.) lives in `domain/types.ts` and mirrors the `Pdg*` GraphQL surface.
 * This file holds only the *UI* concerns those pages own — the KPI band's
 * aggregated stats and the filter bars' state — which have no direct wire
 * equivalent.
 */
import { ConfigurationStatus, FileProcessingStatus } from './domain/enums';
import { ProviderRecentCounts } from './domain/types';

export { FileProcessingStatus };

/** Aggregated counts rendered by the KPI band. */
export interface IngestionStats {
  inFlightCount: number;
  failedCount: number;
  completedCount: number;
  /** BIGINT string forwarded verbatim from the summary — never coerced. */
  totalRowsParsed: string;
}

export interface IngestionFilters {
  statuses: FileProcessingStatus[] | null;
  dataProvider: string | null;
  createdAfter: string | null;
  createdBefore: string | null;
}

export const DEFAULT_INGESTION_FILTERS: IngestionFilters = {
  statuses: null,
  dataProvider: null,
  createdAfter: null,
  createdBefore: null,
};

/**
 * Every non-terminal status. `classifyOutcome` returns `null` for anything
 * it does not recognise, so a status missing from this list is not merely
 * unlabelled — it is dropped from the provider outcome counts entirely, and
 * the provider under-reports how many files it has.
 */
export const IN_FLIGHT_STATUSES: FileProcessingStatus[] = [
  FileProcessingStatus.Pending,
  FileProcessingStatus.Parsing,
  FileProcessingStatus.Validating,
  FileProcessingStatus.Resolving,
  FileProcessingStatus.Finalizing,
  FileProcessingStatus.Paused,
];

export const FAILED_STATUSES: FileProcessingStatus[] = [
  FileProcessingStatus.Failed,
  FileProcessingStatus.Rejected,
];

export const COMPLETED_STATUSES: FileProcessingStatus[] = [
  FileProcessingStatus.Completed,
  FileProcessingStatus.CompletedWithErrors,
];

/**
 * Configurations list filters. PDGS filters by a single status (unlike the
 * ingestions list's KPI quick-filter groups), so this is one value, not a
 * set.
 */
export interface ConfigurationFilters {
  status: ConfigurationStatus | null;
  dataProvider: string | null;
}

export const DEFAULT_CONFIGURATION_FILTERS: ConfigurationFilters = {
  status: null,
  dataProvider: null,
};

/**
 * Overview health per provider. Derived in the UI from a `ProviderSummary`
 * (see `utils/provider-health.utils.ts`); not part of the wire contract.
 * `overdue` from the design spec waits on cadence inference in PDGS.
 */
export type ProviderHealth = 'failing' | 'inactive' | 'current';

/**
 * Provider health values in display order for the overview page's health filter.
 * Maps directly to chip filter options: All / Current / Failing / Inactive.
 */
export const PROVIDER_HEALTH_VALUES: ProviderHealth[] = [
  'current',
  'failing',
  'inactive',
];

/** All-providers table chip filter. */
export type HealthFilterValue = 'all' | ProviderHealth;

/**
 * The five outcome classes the overview and provider detail count by.
 * `CANCELLED` belongs to none: shown wherever a status is shown, counted
 * nowhere.
 */
export type OutcomeClass = keyof ProviderRecentCounts;

/** Display order for legends and KPI rows. */
export const OUTCOME_CLASSES: OutcomeClass[] = [
  'completed',
  'partial',
  'failed',
  'rejected',
  'inFlight',
];
